import React, { useState, useEffect, useRef, useCallback } from 'react'
import Icon from '../Icon'
import './index.less'

const SCALE_STEP = 0.25
const MIN_SCALE = 0.25
const MAX_SCALE = 3
const WHEEL_FACTOR = 0.01

export default function ImageViewer({ src, onClose, onDownload, workId }) {
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const isDragging = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const offsetRef = useRef({ x: 0, y: 0 })
  const scaleRef = useRef(1)
  const maskRef = useRef(null)

  // src 变化时重置状态
  useEffect(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
    offsetRef.current = { x: 0, y: 0 }
    scaleRef.current = 1
  }, [src])

  // ESC 关闭
  useEffect(() => {
    const handleKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  // 滚轮缩放
  useEffect(() => {
    const el = maskRef.current
    if (!el) return
    const handleWheel = e => {
      e.preventDefault()
      const delta = -e.deltaY * WHEEL_FACTOR
      scaleRef.current = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scaleRef.current + delta * scaleRef.current))
      setScale(scaleRef.current)
    }
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [src])

  // 拖动
  const handleMouseDown = useCallback(e => {
    e.preventDefault()
    isDragging.current = true
    dragStart.current = { x: e.clientX - offsetRef.current.x, y: e.clientY - offsetRef.current.y }
  }, [])

  const handleMouseMove = useCallback(e => {
    if (!isDragging.current) return
    const x = e.clientX - dragStart.current.x
    const y = e.clientY - dragStart.current.y
    offsetRef.current = { x, y }
    setOffset({ x, y })
  }, [])

  const handleMouseUp = useCallback(() => {
    isDragging.current = false
  }, [])

  // 触摸拖动
  const handleTouchStart = useCallback(e => {
    if (e.touches.length !== 1) return
    isDragging.current = true
    dragStart.current = {
      x: e.touches[0].clientX - offsetRef.current.x,
      y: e.touches[0].clientY - offsetRef.current.y,
    }
  }, [])

  const handleTouchMove = useCallback(e => {
    if (!isDragging.current || e.touches.length !== 1) return
    e.preventDefault()
    const x = e.touches[0].clientX - dragStart.current.x
    const y = e.touches[0].clientY - dragStart.current.y
    offsetRef.current = { x, y }
    setOffset({ x, y })
  }, [])

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false
  }, [])

  const zoomIn = useCallback(() => {
    scaleRef.current = Math.min(scaleRef.current + SCALE_STEP, MAX_SCALE)
    setScale(scaleRef.current)
  }, [])

  const zoomOut = useCallback(() => {
    scaleRef.current = Math.max(scaleRef.current - SCALE_STEP, MIN_SCALE)
    setScale(scaleRef.current)
  }, [])

  const reset = useCallback(() => {
    scaleRef.current = 1
    setScale(1)
    setOffset({ x: 0, y: 0 })
    offsetRef.current = { x: 0, y: 0 }
  }, [])

  // 点击遮罩关闭（排除拖动误触）
  const handleMaskClick = useCallback(e => {
    if (e.target === maskRef.current) onClose()
  }, [onClose])

  if (!src) return null

  return (
    <div
      ref={maskRef}
      className="image-viewer-mask"
      onClick={handleMaskClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        className="image-viewer-img"
        src={src}
        alt="preview"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          cursor: isDragging.current ? 'grabbing' : 'grab',
        }}
        draggable={false}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      />
      <button className="image-viewer-close" onClick={onClose}>
        <Icon type="zykj-guanbi" />
      </button>
      <div className="image-viewer-toolbar" onClick={e => e.stopPropagation()}>
        <button className="image-viewer-btn" onClick={zoomIn} title="放大">
          <Icon type="icon-fangda1" />
        </button>
        <button className="image-viewer-btn" onClick={reset} title="还原">
          <Icon type="icon-shijidaxiao" />
        </button>
        <button className="image-viewer-btn" onClick={zoomOut} title="缩小">
          <Icon type="icon-suoxiao1" />
        </button>
        <div className="image-viewer-divider" />
        <button className="image-viewer-btn" onClick={() => onDownload(src, workId)} title="下载">
          <Icon type="icon-xiazai1" />
        </button>
      </div>
    </div>
  )
}
